import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { IpSet } from "../../schema/types/ipset/entity";
import { useGetIpSetQuery } from "../../store/api/ipSetApi";
import { inputToIpSet, ipSetToInput, ipSetToPatch } from "./utils";
import { IpSetInput } from "../../schema/types/ipset/input";
import { getValidator } from "./validator";
import { IpSetDetailContent } from "./detailContent";
import { getDirtyChecker } from "./dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";

interface Props {
  ipSet: IpSet;
}
export const IpSetDetailForm: React.FC<Props> = ({ ipSet }) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetIpSetQuery(linksOf(ipSet).self!);

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = ipSetToInput(ipSet);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: ipSetToInput(ipSet) }));
  }, [ipSet.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: IpSetInput) {
    try {
      let body = ipSetToPatch(inputToIpSet(input), { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(ipSet).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Ip Set is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <IpSetDetailContent ipSet={ipSet} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(ipSet).self!} inputToItemFn={inputToIpSet} />
      <CpPreviewModal inputToItemFn={inputToIpSet} kind="ipset" namePath="name" title="Ip Set" />
      <CpDebugModal inputToItemFn={inputToIpSet} kind="ipset" namePath="name" title="Ip Set" />
    </CpFormContext.Provider>
  );
};
